/**
 * cart-bogo-routines.js
 * UI component that renders inside the cart drawer when a customer has items
 * from the trigger collection. Lets them pick free coffrets independently —
 * one entitlement per coffret bought from the trigger collection, spent via
 * a per-card +/- stepper (not a single shared pick). A customer who buys 2
 * coffrets can pick 2 of the same one, or 2 different ones, or split further
 * once they buy more.
 *
 * The component is a custom element placed inside [data-cart-drawer-body]
 * (re-rendered on each Section Rendering refresh). Its Liquid (snippets/
 * cart-bogo-routines.liquid) renders it only when the trigger condition is
 * met, and computes each card's current picked quantity server-side so the
 * stepper always starts in sync with the real cart state.
 *
 * Gift line property: _bogo_routines_gift = "true", _free_gift = "true"
 * (one cart line per distinct picked coffret; quantity is that pick's count)
 *
 * Requires:
 *   - settings.enable_bogo_routines = true
 *   - settings.bogo_routines_trigger_collection (collection)
 *   - settings.bogo_routines_display_collection (coffret products)
 *   - cart-promo-discount Shopify Function, backed by an active automatic
 *     discount — the function grants one free unit per unit bought from the
 *     trigger collection, spread across however many distinct coffrets the
 *     customer picked, cheapest picked unit first if picks ever exceed
 *     entitlement. See .claude/rules/cart-promotions.md.
 */
(function () {
  'use strict';

  var LOCK_OWNER = 'bogo-routines';
  var LOCK_TTL_MS = 10000;

  function acquireGiftLock() {
    var held = window.__lvGiftLock;
    if (held && (Date.now() - held.at) < LOCK_TTL_MS) return false;
    window.__lvGiftLock = { owner: LOCK_OWNER, at: Date.now() };
    return true;
  }

  function releaseGiftLock() {
    var held = window.__lvGiftLock;
    if (held && held.owner === LOCK_OWNER) window.__lvGiftLock = null;
  }

  if (customElements.get('cart-bogo-routines')) return;

  customElements.define('cart-bogo-routines', class extends HTMLElement {
    #isUpdating = false;

    connectedCallback() {
      this.querySelectorAll('[data-bogo-increment]').forEach((btn) => {
        btn.addEventListener('click', (e) => this.#handleStep(e, 1));
      });
      this.querySelectorAll('[data-bogo-decrement]').forEach((btn) => {
        btn.addEventListener('click', (e) => this.#handleStep(e, -1));
      });
      this.querySelectorAll('[data-bogo-select]').forEach((card) => {
        card.addEventListener('click', (e) => this.#handleSelect(e));
        card.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.#handleSelect(e);
          }
        });
      });
    }

    /* Single-pick mode (exactly 1 entitlement) — clicking a card swaps the
       one pick directly instead of requiring a decrement-then-increment
       round trip through the stepper. See snippets/cart-bogo-routines.liquid
       "single_pick_mode". */
    async #handleSelect(e) {
      const card = e.currentTarget;
      if (this.#isUpdating || !card) return;

      const variantId = card.dataset.variantId;
      if (!variantId) return;
      if (!acquireGiftLock()) return;

      this.#isUpdating = true;
      this.#setBusy(true);

      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const items = cart.items || [];

        let matchingItem = null;
        const otherPicks = [];
        items.forEach((item) => {
          if (item.properties && item.properties._bogo_routines_gift === 'true') {
            if (String(item.variant_id) === String(variantId)) {
              matchingItem = item;
            } else {
              otherPicks.push(item);
            }
          }
        });

        if (matchingItem) {
          /* Already the pick — nothing to do. */
          return;
        }

        for (const item of otherPicks) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          });
        }

        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        fd.append('properties[_bogo_routines_gift]', 'true');
        fd.append('properties[_free_gift]', 'true');
        await fetch('/cart/add.js', { method: 'POST', body: fd });

        if (window.cartDrawer && window.cartDrawer.refresh) {
          window.cartDrawer.refresh();
        }
      } catch (err) {
        console.error('[BOGO Routines] Select failed:', err);
      } finally {
        this.#isUpdating = false;
        this.#setBusy(false);
        releaseGiftLock();
      }
    }

    async #handleStep(e, delta) {
      const btn = e.currentTarget;
      if (this.#isUpdating || !btn || btn.disabled) return;

      const card = btn.closest('[data-bogo-coffret-card]');
      const variantId = card && card.dataset.variantId;
      if (!variantId) return;

      const entitlement = parseInt(this.dataset.entitlement, 10) || 0;
      if (!acquireGiftLock()) return;

      this.#isUpdating = true;
      this.#setBusy(true);

      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        const items = cart.items || [];

        let pickedTotal = 0;
        let matchingItem = null;
        items.forEach((item) => {
          if (item.properties && item.properties._bogo_routines_gift === 'true') {
            pickedTotal += item.quantity;
            if (String(item.variant_id) === String(variantId)) matchingItem = item;
          }
        });

        if (delta > 0) {
          /* Can't spend more than the entitlement. */
          if (pickedTotal >= entitlement) return;

          if (matchingItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: matchingItem.key, quantity: matchingItem.quantity + 1 }),
            });
          } else {
            const fd = new FormData();
            fd.append('id', variantId);
            fd.append('quantity', '1');
            fd.append('properties[_bogo_routines_gift]', 'true');
            fd.append('properties[_free_gift]', 'true');
            await fetch('/cart/add.js', { method: 'POST', body: fd });
          }
        } else {
          if (!matchingItem) return;
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: matchingItem.key, quantity: Math.max(0, matchingItem.quantity - 1) }),
          });
        }

        if (window.cartDrawer && window.cartDrawer.refresh) {
          window.cartDrawer.refresh();
        }
      } catch (err) {
        console.error('[BOGO Routines] Step failed:', err);
      } finally {
        this.#isUpdating = false;
        this.#setBusy(false);
        releaseGiftLock();
      }
    }

    #setBusy(busy) {
      this.querySelectorAll('[data-bogo-increment], [data-bogo-decrement], [data-bogo-select]').forEach((btn) => {
        btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      });
    }
  });

  /* ── Reconciler ──────────────────────────────────────────────────────────
   * The stepper only ever asks for +/-1 changes gated by a snapshot of the
   * cart taken at click time, so without a background reconciler a customer
   * could still end up over/under entitlement — e.g. remove a bought coffret
   * after picking gifts (their picks now exceed what they're owed), or buy
   * more of the trigger item (their picks now fall short of what they're
   * owed, and the selector would otherwise sit there under-filled until they
   * manually add more). This keeps the cart's actual gift-line quantities in
   * sync with entitlement on every cart change, the same invariant every
   * other cart-watcher reconciler in this theme enforces.
   *
   * Trim rule (picks > entitlement): reduce the MOST EXPENSIVE picked
   * coffret(s) first. The discount function already protects the merchant
   * regardless (it always zeroes the cheapest picked units first up to
   * entitlement, see cart-promo-discount), so trimming the priciest picks
   * out of the cart first keeps what's actually left in the cart matching
   * what the function would have discounted anyway, rather than showing the
   * customer a stale pricier "free" pick that the function silently didn't
   * discount.
   *
   * Top-up rule (picks < entitlement): default the shortfall onto whichever
   * trigger variant was most recently seen in the cart scan (the coffret the
   * customer just bought) — this is what lets a customer buy more without
   * ever having to reselect a coffret from scratch, matching the original
   * single-pick version of this promo's default-selection behaviour.
   */
  var configEl = document.querySelector('[data-bogo-routines-config]');
  if (!configEl) return;

  var config;
  try { config = JSON.parse(configEl.textContent); } catch (e) { return; }
  if (!config || !config.enabled) return;

  var triggerVariantIds = {};
  (config.triggerVariantIds || []).forEach(function (id) { triggerVariantIds[String(id)] = true; });
  if (Object.keys(triggerVariantIds).length === 0) return;

  var unavailable = {}; /* variantId -> true when /cart/add.js rejected it this session */
  var suppress = false;
  var isUpdating = false;
  var debounceTimer = null;
  var lastKnownBoughtQty = null; /* null until the first reconcile pass has run */

  function refresh() {
    if (!window.cartDrawer || !window.cartDrawer.refresh) return;
    suppress = true;
    var p = window.cartDrawer.refresh();
    if (p && typeof p.then === 'function') {
      p.then(function () { suppress = false; }).catch(function () { suppress = false; });
    } else {
      setTimeout(function () { suppress = false; }, 500);
    }
  }

  function changeQty(item, qty) {
    if (isUpdating) return;
    isUpdating = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: qty })
    })
      .then(function (res) { if (res.ok) refresh(); })
      .catch(function (e) { console.error('[BOGO Routines] Reconcile failed:', e); })
      .then(function () { isUpdating = false; });
  }

  function addDefaultGift(variantId, qty) {
    if (isUpdating || unavailable[variantId]) return;
    if (!acquireGiftLock()) return;
    isUpdating = true;

    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', String(qty));
    fd.append('properties[_bogo_routines_gift]', 'true');
    fd.append('properties[_free_gift]', 'true');

    fetch('/cart/add.js', { method: 'POST', body: fd })
      .then(function (res) {
        if (res.status === 422) { unavailable[variantId] = true; return; }
        if (!res.ok) throw new Error(res.status);
        refresh();
      })
      .catch(function (e) { console.error('[BOGO Routines] Default add failed:', e); })
      .then(function () { isUpdating = false; releaseGiftLock(); });
  }

  function reconcile() {
    if (suppress || isUpdating) return;

    fetch('/cart.js')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;

        var boughtQty = 0;
        var lastTriggerVariantId = null;
        var giftItems = [];

        (cart.items || []).forEach(function (item) {
          var p = item.properties || {};
          if (p._bogo_routines_gift === 'true') { giftItems.push(item); return; }
          /* A gift line never earns another gift. */
          if (p._free_gift === 'true' || p._tier_gift === 'true') return;
          if (triggerVariantIds[String(item.variant_id)]) {
            boughtQty += item.quantity;
            lastTriggerVariantId = String(item.variant_id);
          }
        });

        var pickedTotal = 0;
        giftItems.forEach(function (item) { pickedTotal += item.quantity; });

        if (boughtQty === 0) {
          /* Trigger no longer met at all — drop every pick. */
          lastKnownBoughtQty = 0;
          var overage = giftItems.find(function (item) { return item.quantity > 0; });
          if (overage) changeQty(overage, 0);
          return;
        }

        if (pickedTotal > boughtQty) {
          /* Trim the priciest pick(s) down to fit, one step at a time —
             each trim triggers its own refresh, and the next reconcile pass
             (from that refresh's own cart:updated) continues trimming if
             more than one unit needs to go. Deliberately does NOT touch
             lastKnownBoughtQty here — entitlement itself hasn't changed. */
          var priciest = giftItems.reduce(function (max, item) {
            return !max || item.price > max.price ? item : max;
          }, null);
          if (priciest) changeQty(priciest, priciest.quantity - 1);
          return;
        }

        /* Top up ONLY the entitlement that's newly appeared since the last
           pass — never the full gap between picks and entitlement. Using
           the full gap was a real, confirmed bug: a customer who bought a
           coffret, got it auto-picked, then tried to swap to a different
           one by decrementing it, was fighting this reconciler the whole
           time — every decrement drops pickedTotal below boughtQty for a
           moment, and the old "top up to boughtQty" rule immediately
           re-added the same default coffret before the customer could ever
           increment a different one. Tracking what entitlement this
           reconciler has already SEEN (lastKnownBoughtQty) and only filling
           the delta means a deliberate decrement is respected and stays
           empty (freeing real budget for a different pick), while genuinely
           buying more still auto-fills without requiring a reselect. */
        if (lastKnownBoughtQty === null) lastKnownBoughtQty = 0;
        var newEntitlement = boughtQty - lastKnownBoughtQty;
        if (newEntitlement > 0 && pickedTotal < boughtQty && lastTriggerVariantId) {
          var shortfall = boughtQty - pickedTotal;
          var topUp = Math.min(newEntitlement, shortfall);
          if (topUp > 0) addDefaultGift(lastTriggerVariantId, topUp);
        }
        lastKnownBoughtQty = boughtQty;
      })
      .catch(function (e) { console.error('[BOGO Routines]', e); });
  }

  document.addEventListener('cart:updated', function () {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 200);
  });

  setTimeout(reconcile, 600);
})();
